import { useNavigate } from '@tanstack/react-router'
import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { useAuth } from '~/auth/auth-context'
import { ConfirmDialog } from '~/components/confirm-dialog'
import { DialogLink } from '~/components/dialog-link'
import { FloatOut } from '~/components/float-out'
import { MoreIcon } from '~/components/icons'
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover'
import { nudgeHourReached, weighInComplete, todayKey } from '~/data/weighins'
import { useWeighIns } from '~/data/weighins-store'
import { useWeighInReminder } from '~/lib/weighin-settings'
import { DIALOG_PATHS } from '~/lib/dialogs'

/** The nudge stays away for the rest of the session once it has been waved off. */
const NUDGED_KEY = 'gymcloud.weighin-nudged'

/**
 * The day the nudge was put away for, which outlives the session that put it away — that is
 * the whole of what makes "not today" different from "not now".
 */
const NOT_ON_KEY = 'gymcloud.weighin-nudge-not-on'

/**
 * The daily weigh-in's nudge, and the reminder that goes with it — see the weigh-in spec. It
 * stands under the account in the header, on every page, and is a note of its own: it used to
 * hang off the account's menu, and the menu is gone.
 */
export function WeighInNudge({
  nudgeable,
}: {
  /**
   * Whether the page under the account has room for the note. A set fills the window and the
   * header goes under it — a note hanging off an avatar nobody can see is a stray element, and
   * the nudge is drawn on the body, so nothing the page does covers it any more.
   */
  nudgeable: boolean
}) {
  const { settings, setSettings } = useAuth()
  const navigate = useNavigate()
  const weighIns = useWeighIns()
  const reminder = useWeighInReminder()

  const today = todayKey()
  const todays = (weighIns ?? []).find((weighIn) => weighIn.day === today)
  const weighedToday = todays?.weightKg != null
  /*
   * The nudge asks a wider question than the reminder does: the reminder is about a morning
   * nobody has stepped on the scales for, and the nudge stands until the whole of what the
   * account set the day to log is in.
   */
  const loggedToday = weighInComplete(todays ?? null, settings.weighInRoutine)

  // The daily reminder, while the app is open: a browser notification at the chosen time,
  // for a day that has no weigh-in yet. Firing re-arms the timer for the next day.
  const weighedRef = useRef(weighedToday)
  useEffect(() => {
    weighedRef.current = weighedToday
  }, [weighedToday])
  const [reminderArm, setReminderArm] = useState(0)
  useEffect(() => {
    if (
      !reminder.enabled ||
      typeof Notification === 'undefined' ||
      Notification.permission !== 'granted'
    ) {
      return
    }
    const [hours = 8, minutes = 0] = reminder.time.split(':').map(Number)
    const now = new Date()
    const at = new Date(now)
    at.setHours(hours, minutes, 0, 0)
    if (at.getTime() <= now.getTime()) {
      at.setDate(at.getDate() + 1)
    }
    const timer = window.setTimeout(() => {
      if (!weighedRef.current) {
        const note = new Notification('Daily weigh-in', {
          body: "Today's weigh-in is still unlogged.",
        })
        // Pressing it opens the day it was about, over whatever page is showing.
        note.onclick = () => {
          window.focus()
          note.close()
          void navigate({
            to: '.',
            search: (prev) => ({ ...prev, dialog: 'weighin-weight' as const }),
            mask: { to: DIALOG_PATHS['weighin-weight'] },
            resetScroll: false,
            viewTransition: false,
          })
        }
      }
      setReminderArm((held) => held + 1)
    }, at.getTime() - now.getTime())
    return () => {
      window.clearTimeout(timer)
    }
  }, [reminder.enabled, reminder.time, reminderArm, navigate])

  // The nudge: waved off, it stays away for the session; "never" turns the setting off.
  const [nudged, setNudged] = useState(() => {
    try {
      return sessionStorage.getItem(NUDGED_KEY) === '1'
    } catch {
      return false
    }
  })
  const waveOff = () => {
    setNudged(true)
    try {
      sessionStorage.setItem(NUDGED_KEY, '1')
    } catch {
      // The session's own state already has it.
    }
  }
  const [notOn, setNotOn] = useState(() => {
    try {
      return localStorage.getItem(NOT_ON_KEY)
    } catch {
      return null
    }
  })
  const putAwayForTheDay = () => {
    const day = todayKey()
    setNotOn(day)
    try {
      localStorage.setItem(NOT_ON_KEY, day)
    } catch {
      // This browser's own state already has it, for as long as it is open.
    }
  }
  const [confirmingNever, setConfirmingNever] = useState(false)
  const [more, setMore] = useState(false)

  return (
    <>
      {/* Drawn on the body: it is written inside the blurred header, whose backdrop filter is
          a stacking context, so its layer would otherwise mean nothing to anything the page
          drew above the header. */}
      {createPortal(
        /*
         * It arrives and it leaves, both by the same easing every other floating panel takes:
         * fading and unblurring down into place, and fading and blurring back up on the way
         * out. Unhurried rather than brisk — a menu answers a press and only has to catch up
         * with a hand already moving, while this appears in a corner nobody was looking at,
         * and the movement is what brings the eye over.
         *
         * Leaving matters more than arriving here. Waved off, the note used to be gone between
         * one frame and the next, which reads as something being taken away rather than as an
         * answer to the press.
         */
        <FloatOut
          open={
            nudgeable &&
            settings.weighInNudge &&
            !nudged &&
            weighIns !== null &&
            notOn !== todayKey() &&
            nudgeHourReached(settings.weighInNudgeFrom) &&
            !loggedToday
          }
          pace="unhurried"
        >
          {/* Already a surface, so the hover brightens it a step — never the translucent
              wash, which would let the page bleed through the note. */}
          <div className="fixed top-[4.5rem] right-4 z-40 w-72 rounded-2xl bg-surface-overlay p-3 shadow-2xl ring-1 ring-white/10 transition-colors hover:bg-surface-bright">
            {/* The whole note is the way in; its own controls ride above that reach. */}
            <div className="flex items-start gap-2">
              <DialogLink
                dialog="weighin-weight"
                className="block min-w-0 flex-1 text-sm after:absolute after:inset-0 after:rounded-2xl"
              >
                Today's weigh-in is still unlogged.
              </DialogLink>
              {/* The choice worth a second thought waits behind the dots. */}
              <Popover open={more} onOpenChange={setMore}>
                <PopoverTrigger
                  aria-label="More"
                  className="relative z-10 -mt-1 -mr-1 rounded-full p-1 text-ink-muted transition-colors hover:bg-hover-strong hover:text-ink"
                >
                  <MoreIcon aria-hidden className="size-5" />
                </PopoverTrigger>
                <PopoverContent align="end" className="w-56 p-2">
                  {/*
                    Between putting it away for now and turning it off for good, which were the
                    only two answers. Somebody who is not weighing in today has neither of those
                    to say: "Not now" is back on the next load, and turning it off is about
                    every day rather than this one.
                  */}
                  <button
                    type="button"
                    className="menu-row"
                    onClick={() => {
                      setMore(false)
                      putAwayForTheDay()
                    }}
                  >
                    Not today
                  </button>
                  <button
                    type="button"
                    className="menu-row"
                    onClick={() => {
                      setMore(false)
                      setConfirmingNever(true)
                    }}
                  >
                    Never show this again
                  </button>
                </PopoverContent>
              </Popover>
            </div>
            {/* The one visible way to say later: a secondary action, outlined like every other. */}
            <div className="relative z-10 mt-2 flex justify-end">
              <button
                type="button"
                className="rounded-full border border-outline px-3 py-1 text-sm transition-colors hover:bg-hover"
                onClick={waveOff}
              >
                Not now
              </button>
            </div>
          </div>
        </FloatOut>,
        document.body,
      )}

      {confirmingNever && (
        <ConfirmDialog
          title="Never show the nudge again?"
          body="The nudge stays off until it is turned back on in Settings → Weigh-in. Hiding it instead keeps it away for now and back another day."
          confirm="Turn it off"
          alt="Just hide it for now"
          onAlt={() => {
            setConfirmingNever(false)
            waveOff()
          }}
          onConfirm={() => {
            setConfirmingNever(false)
            void setSettings({ weighInNudge: false })
          }}
          onDismiss={() => {
            setConfirmingNever(false)
          }}
        />
      )}
    </>
  )
}
